import { useState } from "react";
import { ChevronRight } from "lucide-react";

/**
 * PipelineGroupedPills — Approche A (pills avec expansion)
 * 
 * Props:
 *   stages: Array<{ id, nom, color, groupe_pipeline }>
 *   groups: Array<{ id, label, color, ordre }>
 *   counts: Object<stageId, number>      — client count per stage
 *   selectedStage: string|null            — currently selected stage id
 *   onSelectStage: (stageId|null) => void — toggle a stage
 *   horizontal: boolean                   — horizontal layout (default true)
 */
export default function PipelineGroupedPills({
  stages = [],
  groups = [],
  counts = {},
  selectedStage = null,
  onSelectStage,
  horizontal = true,
}) {
  const [expandedGroup, setExpandedGroup] = useState(null);

  // Build grouped structure
  const sortedGroups = [...groups].sort((a, b) => (a.ordre || 99) - (b.ordre || 99));
  const groupedStages = {};
  const ungrouped = [];

  stages.forEach((s) => {
    if (s.groupe_pipeline && groups.some((g) => g.id === s.groupe_pipeline)) {
      if (!groupedStages[s.groupe_pipeline]) groupedStages[s.groupe_pipeline] = [];
      groupedStages[s.groupe_pipeline].push(s);
    } else {
      ungrouped.push(s);
    }
  });

  const groupTotal = (gid) =>
    (groupedStages[gid] || []).reduce((sum, s) => sum + (counts[s.id] || 0), 0);

  const handleGroupClick = (gid) => {
    setExpandedGroup(expandedGroup === gid ? null : gid);
  };

  const handlePillClick = (stageId) => {
    if (onSelectStage) onSelectStage(selectedStage === stageId ? null : stageId);
  };

  return (
    <div data-testid="pipeline-grouped-pills">
      {/* Group pills row */}
      <div className={`flex ${horizontal ? "flex-row" : "flex-col"} flex-wrap gap-2 mb-2`}>
        {sortedGroups
          .filter((g) => (groupedStages[g.id] || []).length > 0)
          .map((g) => {
            const total = groupTotal(g.id);
            const isExpanded = expandedGroup === g.id;
            return (
              <button
                key={g.id}
                data-testid={`group-pill-${g.id}`}
                onClick={() => handleGroupClick(g.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-all ${
                  isExpanded ? "ring-2 ring-white/20 shadow-lg scale-105" : "hover:scale-105"
                }`}
                style={{
                  backgroundColor: isExpanded ? g.color : `${g.color}15`,
                  color: isExpanded ? "#fff" : g.color,
                  borderWidth: 1,
                  borderColor: isExpanded ? g.color : `${g.color}30`,
                }}
              >
                {g.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                    isExpanded ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300"
                  }`}
                >
                  {total}
                </span>
                <ChevronRight
                  className={`w-3 h-3 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                />
              </button>
            );
          })}

        {/* Ungrouped pills inline */}
        {ungrouped.map((s) => {
          const count = counts[s.id] || 0;
          const isSelected = selectedStage === s.id;
          return (
            <button
              key={s.id}
              data-testid={`stage-pill-${s.id}`}
              onClick={() => handlePillClick(s.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
                isSelected ? "ring-2 ring-white/30 shadow-lg scale-105" : "hover:scale-105 hover:shadow-md"
              }`}
              style={{
                backgroundColor: isSelected ? s.color : `${s.color}20`,
                color: isSelected ? "#fff" : s.color,
                borderWidth: 1,
                borderColor: isSelected ? s.color : `${s.color}40`,
              }}
            >
              {s.nom}
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  isSelected ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300"
                }`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Expanded sub-pills */}
      {expandedGroup && groupedStages[expandedGroup] && (
        <div
          data-testid={`group-expanded-${expandedGroup}`}
          className="bg-slate-900/50 rounded-lg border border-slate-700/50 p-3 mb-2"
        >
          <div className="flex flex-wrap gap-2">
            {groupedStages[expandedGroup].map((s) => {
              const count = counts[s.id] || 0;
              const isSelected = selectedStage === s.id;
              const groupColor =
                groups.find((g) => g.id === expandedGroup)?.color || s.color;
              return (
                <button
                  key={s.id}
                  data-testid={`sub-pill-${s.id}`}
                  onClick={() => handlePillClick(s.id)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all ${
                    isSelected ? "ring-2 ring-white/30 shadow-lg scale-105" : "hover:scale-105"
                  }`}
                  style={{
                    backgroundColor: isSelected ? (s.color || groupColor) : `${s.color || groupColor}20`,
                    color: isSelected ? "#fff" : s.color || groupColor,
                    borderWidth: 1,
                    borderColor: isSelected ? (s.color || groupColor) : `${s.color || groupColor}40`,
                  }}
                >
                  {s.nom}
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                      isSelected ? "bg-white/20 text-white" : "bg-slate-800 text-slate-300"
                    }`}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
